import { useCallback, useMemo, useState } from 'react';
import { Button, ButtonSizes, ButtonVariants, Dropdown, Table } from '@carboledger/ui';
import { buildTableLabels } from '@/config/tableLabels';
import { ConfirmDialog } from '@/components/feedback';
import { useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { useGetSiteMaterialsQuery, useRemoveSiteMaterialMutation } from '../../api/sitesApi';
import { buildSiteMaterialsColumns } from '../../constants/siteMaterialsColumns';
import { filterSiteMaterials } from '../../utils/sitesUtils';
import styles from './SiteMaterialsTabView.module.scss';

const getMaterialId = (row) => row.id;

/**
 * Props:
 *   site               the site detail view model — its terminals scope this tab
 *   onAssignMaterials  opens the assign-materials modal the page owns
 */
export const SiteMaterialsTabView = ({ site, onAssignMaterials }) => {
  const t = useTranslations();
  const [search, setSearch] = useState('');
  const [terminalId, setTerminalId] = useState(() => String(site.terminals[0]?.id ?? ''));
  const [materialToRemove, setMaterialToRemove] = useState(null);

  const { data, isFetching } = useGetSiteMaterialsQuery(site.id);
  const [removeSiteMaterial] = useRemoveSiteMaterialMutation();

  const materials = data ?? EMPTY_LIST;
  const hasMaterials = materials.length > 0;

  const handleSearchChange = useCallback((event) => setSearch(event.target.value), []);
  const handleTerminalChange = useCallback((event) => setTerminalId(event.target.value), []);

  const handleRemoveMaterial = useCallback((row) => setMaterialToRemove(row), []);
  const handleCancelRemove = useCallback(() => setMaterialToRemove(null), []);
  const handleConfirmRemove = useCallback(async () => {
    await removeSiteMaterial({
      siteId: site.id,
      materialId: materialToRemove.id,
      terminalId,
    });
    setMaterialToRemove(null);
  }, [removeSiteMaterial, site.id, materialToRemove, terminalId]);

  const columns = useMemo(
    () => buildSiteMaterialsColumns(t, { onRemove: handleRemoveMaterial }),
    [t, handleRemoveMaterial],
  );

  const rows = useMemo(
    () => filterSiteMaterials(materials, { terminalId, search }),
    [materials, terminalId, search],
  );

  const terminalOptions = useMemo(
    () =>
      site.terminals.map((terminal) => ({
        value: String(terminal.id),
        label: terminal.terminalName,
      })),
    [site.terminals],
  );

  const emptyState = useMemo(() => {
    if (hasMaterials) {
      return {
        title: t('MATERIALS_NO_RESULTS_TITLE'),
        description: t('COMMON_NO_RESULTS_DESCRIPTION'),
      };
    }

    return {
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('MATERIALS_EMPTY_TITLE'),
      description: t('MATERIALS_EMPTY_DESCRIPTION'),
      primaryLabel: t('SITES_ACTION_ASSIGN_MATERIALS'),
      onPrimaryClick: onAssignMaterials,
    };
  }, [t, hasMaterials, onAssignMaterials]);

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  return (
    <div className={styles.card}>
      {hasMaterials && (
        <div className={styles.toolbar}>
          <div className={styles.scope}>
            <Dropdown
              className={styles.terminalSelect}
              value={terminalId}
              onChange={handleTerminalChange}
              options={terminalOptions}
              placeholder={t('FORM_SELECT_TERMINAL')}
              emptyMessage={t('FORM_TERMINAL_EMPTY')}
              aria-label={t('FIELD_TERMINAL_NAME')}
            />
            <input
              type="search"
              className={styles.search}
              value={search}
              placeholder={t('TABLE_SEARCH')}
              aria-label={t('TABLE_SEARCH')}
              onChange={handleSearchChange}
            />
          </div>

          <Button
            variant={ButtonVariants.primary}
            size={ButtonSizes.sm}
            onClick={onAssignMaterials}
          >
            {t('SITES_ACTION_ASSIGN_MATERIALS')}
          </Button>
        </div>
      )}

      <Table
        columns={columns}
        data={rows}
        getRowId={getMaterialId}
        loading={isFetching}
        emptyState={emptyState}
        labels={tableLabels}
      />

      <ConfirmDialog
        open={materialToRemove !== null}
        title={t('MATERIALS_REMOVE_TITLE')}
        bodyTitle={t('MATERIALS_REMOVE_BODY_TITLE', {
          materialName: materialToRemove?.materialName,
        })}
        description={t('MATERIALS_REMOVE_DESCRIPTION')}
        confirmLabel={t('COMMON_REMOVE')}
        tone="danger"
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
      />
    </div>
  );
};
